import { AccountFeatures, AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React, { useEffect, useState } from "react"
import { Alert, Col, Row } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import { useQuery } from "react-query"
import { Link } from "react-router-dom"
import Toggle from "react-toggle"
import GlobalHelper from "../../../common/GlobalHelper.jsx"
import Icon from "../../../common/icon/icon.component.jsx"
import { SelectOption } from "../../../common/types"
import { ExternalPaymentTypesMapping } from "../../../payment/externalpaymenttypesmapping.jsx"
import { isError } from "../../../state/helpers/Error"
import { ExternalPaymentMethodType } from "../../../state/integration/ExternalPaymentMethodType"
import { IntegrationConfigurationAttributes } from "../../../state/integration/IntegrationConfiguration"
import IntegrationConfigurationService from "../../../state/integration/IntegrationConfigurationService"
import Location from "../../../state/location/Location.js"
import LocationService from "../../../state/location/LocationService"
import LocationSelect from "../../LocationSelect"

type OwnProps = {
    integrationConfigurationId: string,
    configuration: IntegrationConfigurationAttributes,
    locationIds: string[],
    onLocationChange: (newLocationIds: SelectOption<string>[]) => void,
    enabledFeatures,
    onFeatureToggled: (featureKey: string) => void,
}

const StripeFormComponent = (props: OwnProps) => {
    const { integrationConfigurationId, configuration, locationIds, onLocationChange, enabledFeatures, onFeatureToggled } = props

    const [paymentMethodTypes, setPaymentMethodTypes] = useState<ExternalPaymentMethodType[]>([])
    const [integrationLocationIds, setIntegrationLocationIDs] = useState<SelectOption<string>[]>([])
    const [locations, setLocations] = useState<Location[]>([])
    const { isLoading: isLoadingLocations } = useQuery(["getLocations"], async () => {
        return LocationService.fetchAllLocations()
    }, {
        onSuccess: (locations: Location[]) => {
            locationIds.includes("") ? setIntegrationLocationIDs([{
                value: "",
                label: <FormattedMessage
                    id="common.location.select.all_unassigned_options"
                    defaultMessage="All unassigned locations" />
            }]) :
                setIntegrationLocationIDs(locationIds.map(id => {
                    return {
                        value: id,
                        label: locations.find(loc => loc.getId() === id)?.getName() as string
                    }
                }))
            const availableLocations = locations.filter((location) => (location.getEnabled() && !GlobalHelper.isLocationAssigned(location.getId()))
                || locationIds.includes(location.getId()))
            setLocations(availableLocations)
        },
    })

    useEffect(() => {
        if (!integrationConfigurationId) {
            return
        }

        (async () => {
            const res = await IntegrationConfigurationService.retrieveExternalPaymentTypes(integrationConfigurationId)
            if (!isError(res)) {
                setPaymentMethodTypes(res.data)
            }
        })()

    }, [integrationConfigurationId])

    const onLocationSelect = (selectedlocationsIDs: SelectOption<string>[]) => {
        if (!selectedlocationsIDs || selectedlocationsIDs.length === 0) {
            setIntegrationLocationIDs([])
            return
        }
        setIntegrationLocationIDs((prevSelectedValues) => {
            const newSelection = !prevSelectedValues.find(i => i.value === "")
                ? (selectedlocationsIDs.find(i => i.value === "") ? [{
                    value: "", label:
                        <FormattedMessage
                            id="common.location.select.all_unassigned_options"
                            defaultMessage="All unassigned locations" />
                } as SelectOption<string>] : selectedlocationsIDs) : selectedlocationsIDs.filter(i => i.value !== "")
            onLocationChange(newSelection)
            return newSelection
        })
    }



    const url = AppContext.getOptions().apiUrl + "/openid/connect/stripe/" +
        AppContext.getUserLoggedIn().id + (integrationConfigurationId ? `?configurationId=${integrationConfigurationId}` : "")

    const connectButton =
        <div className="form-group row">
            <label className="col-3 col-form-label text-align-right">
                <FormattedMessage
                    id="integration.form.edit.stripe.connect"
                    defaultMessage="Connect"
                />
            </label>
            <div className={"col-9"}>
                <div>
                    {
                        ((!configuration.accountId && !configuration.publicKey && !configuration.secretKey) ||
                            (!configuration.accountId && !configuration.testMode)) &&
                        <a href={url}>
                            <img src={"/images/stripe_connect_btn_en.png"} height={"40px"} />
                        </a>
                    }
                    {
                        !configuration.accountId && configuration.publicKey &&
                        configuration.secretKey && configuration.testMode &&
                        <small className="form-text text-danger mt-2">
                            <Icon value={"exclamation"} className={"ml-1"} />
                            <FormattedMessage
                                id="integration.form.edit.stripe.deprecated_test_account_warning"
                                defaultMessage="This integration was linked to a test
                                account and is now deprecated. Please create a new one."
                            />
                        </small>
                    }
                    {
                        (integrationConfigurationId !== "" && !configuration.accountId &&
                            configuration.publicKey && configuration.secretKey && !configuration.testMode) &&
                        <small className="form-text text-danger">
                            <Icon value={"exclamation"} className={"ml-1"} />
                            <FormattedMessage
                                id="integration.form.edit.stripe.reconnect_upgrade_warning"
                                defaultMessage="You need to reconnect your account to upgrade your integration."
                            />
                        </small>}
                    {
                        (integrationConfigurationId !== "" && !configuration.accountId &&
                            !configuration.publicKey && !configuration.secretKey && !configuration.testMode) &&
                        <small className="form-text text-danger">
                            <Icon value={"exclamation"} className={"ml-1"} />
                            <FormattedMessage
                                id="integration.form.edit.stripe.not_connected_warning"
                                defaultMessage="You have not connected your Stripe account to this integration yet."
                            />
                        </small>
                    }
                </div>
            </div>
        </div>

    if (!integrationConfigurationId || (!configuration.accountId && AppContext.getOptions().environment === "prod")) {
        return <div className="col-12 ui-module">
            {GlobalHelper.getCountryCodeFromCurrency(AppContext.getCurrency()) === "US" && <Row className={"mb-2"}>
                <Col sm={{span: 7, offset: 3}}>
                    <Alert variant={"warning"}>
                        <FormattedMessage
                            id="integration.form.edit.stripe.description"
                            defaultMessage="A surcharge of 0.45% is added to credit card transactions processed with Stripe.
                                We invite you to explore our payment processing solution"
                        />
                        &nbsp;
                        <Link to={"/payment-processing"}>
                            <FormattedMessage
                                id="integration.form.edit.stripe.link_to_payment_processing"
                                defaultMessage="QuoteMachine Payments"
                            />
                        </Link>
                    </Alert>
                </Col>
            </Row>}
            {connectButton}
            {
                AppContext.getUserLoggedIn().attributes.superUser &&
                !integrationConfigurationId &&
                <button className="btn btn-primary offset-3" onClick={CreateTestAccount}>
                    Create Stripe test account
                </button>
            }
        </div>
    }

    const integratedPaymentBankTransferFeature = "integrated-payment-bank-transfer"

    return <div className="col-12 ui-module">
        {configuration.testMode && <div className="form-group row">
            <label className="col-3 col-form-label text-align-right">
            </label>
            <div className={"col-9 text-danger"}>
                <Icon value={"exclamation"} />
                <FormattedMessage
                    id="integration.form.edit.stripe.test_account_warning"
                    defaultMessage="This is a test account."
                />
            </div>
        </div>}
        <div className="form-group row">
            <label className="col-3 col-form-label text-align-right" htmlFor="stripe_accountId">
                <FormattedMessage
                    id="integration.form.edit.stripe.account_id_label"
                    defaultMessage="Stripe Account ID"
                />
            </label>
            <div className="col-9">
                <input
                    disabled={true}
                    name="stripe_accountId"
                    value={configuration.accountId}
                    id="stripe_accountId"
                    type="text"
                    className="form-control"
                />
            </div>
        </div>
        {
            GlobalHelper.canEditLocation() ? <div className="form-group row">
                <label className="col-3 col-form-label text-align-right" htmlFor="locationId">
                    <FormattedMessage
                        id="integration.form.edit.stripe.locations_label"
                        defaultMessage="Location(s)"
                    />
                </label>
                {locations.length == 0 && !isLoadingLocations ?
                    <div className={"col-9 d-flex align-items-center"}>
                        <FormattedMessage
                            id="integration.form.edit.stripe.no_locations"
                            defaultMessage="Unable to assign locations to the integration as none are available.
                        Please ensure you have either enabled locations or unassigned enabled locations."
                        />
                    </div>
                    :
                    <div className={"col-9"}>
                        <>
                            <LocationSelect
                                locations={locations}
                                isLoadingLocations={isLoadingLocations}
                                selectedLocationIds={integrationLocationIds}
                                onSelect={(selectedlocationsIDs) => {
                                    onLocationSelect(selectedlocationsIDs)
                                }}
                                multi={true}
                            />
                            <small className="form-text text-muted">
                                <FormattedMessage
                                    id="integration.form.edit.stripe.locations_description"
                                    defaultMessage="Allow integration for specific or all locations"
                                />
                            </small>
                        </>
                    </div>
                }
            </div> : ""
        }
        {
            AppContext.accountFeatureIsEnabled(AccountFeatures.FeatureIntegratedBankTransfer) ?
                <div className="form-group row" key={integratedPaymentBankTransferFeature}>
                    <label className="col-3 col-form-label text-align-right" htmlFor={integratedPaymentBankTransferFeature}>
                        <FormattedMessage
                            id="integration.form.edit.feature_toggle.integrated_payment_bank_transfer_description"
                            defaultMessage="Bank transfer payment (ACH Direct Debit)"
                        />
                    </label>
                    <div className="col-9 d-flex align-items-center">
                        <Toggle
                            id={integratedPaymentBankTransferFeature}
                            defaultChecked={enabledFeatures[integratedPaymentBankTransferFeature]}
                            icons={false}
                            onChange={() => {
                                onFeatureToggled(integratedPaymentBankTransferFeature)
                            }} />
                    </div>
                </div> : null
        }
        {
            enabledFeatures &&
            paymentMethodTypes.map(p => {
                const feature = "payment-method-type-" + p.key

                return <div className="form-group row" key={feature}>
                    <label className="col-3 col-form-label text-align-right" htmlFor={feature}>
                        {ExternalPaymentTypesMapping[p.key]}
                    </label>
                    <div className="col-9 d-flex align-items-center">
                        <Toggle
                            id={p.key}
                            defaultChecked={enabledFeatures[p.key]}
                            icons={false}
                            onChange={() => {
                                onFeatureToggled(p.key)
                            }} />
                    </div>
                </div>
            })
        }

    </div>

}

const CreateTestAccount = (event) => {
    event.preventDefault()
    // SECURITY FIX: Backend injects test keys (Bugcrowd #4cefa42c)
    AppContext.getApiClient().post("/integration-configurations", {
        configuration: {},
        integrationKey: "stripe"
    }).then(res => {
        window.location.href = `/integrations/edit/${res.id}`
    })
}

export default injectIntl(StripeFormComponent)


// WEBPACK FOOTER //
// ./src/integration/form/stripe/StripeFormComponent.tsx